import { useEffect, useMemo } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, FocusEvent as ReactFocusEvent } from 'react';
import { Editor, Extension, Mark, mergeAttributes } from '@tiptap/core';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import ListItem from '@tiptap/extension-list-item';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import clsx from 'clsx';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Strikethrough
} from 'lucide-react';
import { sanitizeHtml, toPlainText } from '@/utils/richText';
import './RichTextEditor.css';

type ToolbarButtonKey =
  | 'bold'
  | 'italic'
  | 'underline'
  | 'strike'
  | 'bulletList'
  | 'orderedList';

const DEFAULT_BUTTONS: ToolbarButtonKey[] = [
  'bold',
  'italic',
  'underline',
  'strike',
  'bulletList',
  'orderedList'
];

interface ToolbarButton {
  key: ToolbarButtonKey;
  label: string;
  icon: React.ReactNode;
  isActive: (editor: Editor) => boolean;
  command: (editor: Editor) => void;
}

export interface RichTextEditorProps {
  value?: string;
  onChange?: (html: string, text: string) => void;
  onUpdate?: (payload: { editor: Editor; html: string; text: string }) => void;
  placeholder?: string;
  readOnly?: boolean;
  minHeight?: number;
  className?: string;
  toolbarAccessory?: React.ReactNode;
  toolbarButtons?: ToolbarButtonKey[];
  autoFocus?: boolean;
  onKeyDown?: (event: ReactKeyboardEvent<HTMLDivElement>, editor: Editor) => void;
  onFocus?: (editor: Editor, event: ReactFocusEvent<HTMLDivElement>) => void;
  onBlur?: (editor: Editor, event: ReactFocusEvent<HTMLDivElement>) => void;
}

const toolbarButtonConfig: ToolbarButton[] = [
  {
    key: 'bold',
    label: 'Bold',
    icon: <Bold className="h-4 w-4" />,
    isActive: (editor) => editor.isActive('bold'),
    command: (editor) => editor.chain().focus().toggleBold().run()
  },
  {
    key: 'italic',
    label: 'Italic',
    icon: <Italic className="h-4 w-4" />,
    isActive: (editor) => editor.isActive('italic'),
    command: (editor) => editor.chain().focus().toggleItalic().run()
  },
  {
    key: 'underline',
    label: 'Underline',
    icon: <UnderlineIcon className="h-4 w-4" />,
    isActive: (editor) => editor.isActive('underline'),
    command: (editor) => editor.chain().focus().toggleUnderline().run()
  },
  {
    key: 'strike',
    label: 'Strikethrough',
    icon: <Strikethrough className="h-4 w-4" />,
    isActive: (editor) => editor.isActive('strike'),
    command: (editor) => editor.chain().focus().toggleStrike().run()
  },
  {
    key: 'bulletList',
    label: 'Bullet List',
    icon: <List className="h-4 w-4" />,
    isActive: (editor) => editor.isActive('bulletList'),
    command: (editor) => editor.chain().focus().toggleBulletList().run()
  },
  {
    key: 'orderedList',
    label: 'Numbered List',
    icon: <ListOrdered className="h-4 w-4" />,
    isActive: (editor) => editor.isActive('orderedList'),
    command: (editor) => editor.chain().focus().toggleOrderedList().run()
  }
];

const sanitizeForEditor = (value?: string): string => {
  if (!value) return '<p></p>';
  
  // If value is already HTML with links, preserve them
  const sanitized = sanitizeHtml(value);
  if (!sanitized || sanitized === '<p></p>' || sanitized.trim() === '') {
    return '<p></p>';
  }
  
  // Ensure links are preserved - DOMPurify should already do this but double-check
  return sanitized;
};

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  onUpdate,
  placeholder = 'Type here…',
  readOnly = false,
  minHeight = 120,
  className,
  toolbarAccessory,
  toolbarButtons = DEFAULT_BUTTONS,
  autoFocus = false,
  onKeyDown,
  onFocus,
  onBlur
}) => {
  const MentionMark = useMemo(
    () =>
      Mark.create({
        name: 'mention',
        group: 'inline',
        inline: true,
        selectable: false,
        excludes: '_',
        parseHTML() {
          return [{ tag: 'span.mention' }];
        },
        addAttributes() {
          return {
            'data-mention-id': {
              default: null
            },
            'data-mention-label': {
              default: null
            }
          };
        },
        renderHTML({ HTMLAttributes }) {
          return [
            'span',
            mergeAttributes(
              {
                class: 'mention',
                'data-mention': 'true'
              },
              HTMLAttributes
            ),
            0
          ];
        }
      }),
    []
  );

  const extensions: Extension[] = useMemo(
    () => [
      StarterKit.configure({
        orderedList: { keepMarks: true, keepAttributes: true },
        bulletList: { keepMarks: true, keepAttributes: true },
        listItem: false
      }),
      ListItem,
      Underline,
      MentionMark,
      Link.configure({
        openOnClick: true,
        HTMLAttributes: {
          class: 'editor-link',
          target: '_blank',
          rel: 'noopener noreferrer'
        }
      }),
      Placeholder.configure({
        placeholder,
        emptyEditorClass:
          'before:content-[attr(data-placeholder)] before:text-gray-400 before:float-left before:pointer-events-none before:h-0'
      })
    ],
    [MentionMark, placeholder]
  );

  const editor = useEditor({
    extensions,
    content: sanitizeForEditor(value),
    editable: !readOnly,
    autofocus: autoFocus ? 'end' : false,
    editorProps: {
      attributes: {
        class:
          'focus:outline-none prose prose-sm max-w-none text-gray-800 dark:text-gray-100'
      },
      handlePaste: (view, event) => {
        const clipboardData = event.clipboardData;
        if (!clipboardData) return false;

        const pastedText = clipboardData.getData('text/plain');
        if (!pastedText) return false;

        // Check if it's a URL
        const urlMatch = pastedText.trim().match(/^https?:\/\/.+/);
        if (urlMatch) {
          event.preventDefault();
          const url = pastedText.trim();
          const { state, dispatch } = view;
          const { from } = state.selection;
          
          // Insert as link
          if (state.schema.marks.link) {
            const linkMark = state.schema.marks.link.create({ href: url });
            dispatch(
              state.tr
                .insertText(url, from)
                .addMark(from, from + url.length, linkMark)
            );
          } else {
            dispatch(state.tr.insertText(url, from));
          }
          return true;
        }

        return false;
      }
    },
    onUpdate: ({ editor }) => {
      const html = sanitizeHtml(editor.getHTML());
      const text = editor.getText();
      onChange?.(html, text);
      onUpdate?.({ editor, html, text });
    }
  });

  useEffect(() => {
    if (!editor) return;
    const nextValue = sanitizeForEditor(value);
    const currentValue = sanitizeHtml(editor.getHTML());
    
    // Handle empty value - clear the editor
    if ((!value || value === '' || nextValue === '<p></p>') && currentValue !== '<p></p>') {
      editor.commands.clearContent();
      return;
    }
    
    // Only update if the value actually changed (avoid unnecessary updates that might strip links)
    if (nextValue !== currentValue && nextValue !== '<p></p>') {
      const selection = editor.state.selection;
      // Set content while preserving links and other formatting
      editor.commands.setContent(nextValue, false, {
        preserveWhitespace: 'full'
      });
      // Try to restore selection if possible
      try {
        const docSize = editor.state.doc.content.size;
        if (selection.from <= docSize && selection.to <= docSize) {
          editor.commands.setTextSelection({
            from: Math.min(selection.from, docSize),
            to: Math.min(selection.to, docSize)
          });
        }
      } catch (e) {
        // Ignore selection errors
      }
    }
  }, [editor, value]);

  useEffect(() => {
    return () => {
      editor?.destroy();
    };
  }, [editor]);

  if (!editor) return null;

  const buttonsToRender = toolbarButtons
    .map((key) => toolbarButtonConfig.find((btn) => btn.key === key))
    .filter((btn): btn is ToolbarButton => Boolean(btn));

  return (
    <div className={clsx('rounded-md border border-gray-300 bg-white', className)}>
      {!readOnly && (
        <div className="flex items-center justify-between border-b border-gray-200 px-2 py-1.5">
          <div className="flex items-center gap-1">
            {buttonsToRender.map((button) => (
              <button
                key={button.key}
                type="button"
                onClick={() => button.command(editor)}
                className={clsx(
                  'inline-flex h-8 w-8 items-center justify-center rounded transition-colors',
                  button.isActive(editor)
                    ? 'bg-blue-100 text-blue-600'
                    : 'text-gray-600 hover:bg-gray-100'
                )}
                title={button.label}
                aria-label={button.label}
              >
                {button.icon}
              </button>
            ))}
          </div>
          {toolbarAccessory && <div className="flex items-center">{toolbarAccessory}</div>}
        </div>
      )}

      <div className="px-3 py-2">
        <EditorContent
          editor={editor}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && event.shiftKey && !event.defaultPrevented) {
              event.preventDefault();
              editor.chain().focus().splitBlock().run();
              return;
            }
            onKeyDown?.(event, editor);
          }}
          onFocus={(event) => onFocus?.(editor, event)}
          onBlur={(event) => onBlur?.(editor, event)}
          className={clsx('rich-text-editor-content', readOnly && 'pointer-events-none')}
          style={{ minHeight }}
          data-placeholder={placeholder}
        />
      </div>
    </div>
  );
};

export const isRichTextEmpty = (html: string | undefined | null): boolean => {
  if (!html) return true;
  const sanitized = sanitizeHtml(html);
  const plain = toPlainText(sanitized);
  return !plain.trim();
};

